import React, { useRef } from "react";
import { useChatKit } from './hooks/useChatKit';
import { chatBackendUrl } from './config/config';
import { getReportPdfFilename } from './utils/reportArtifact';
import { FiDownload, FiLoader, FiX, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import './neuroa-side-panel.css';

/** Side panel for the ChatKit conversation and its active report. */
export default function NeuroaSidePanel({
  sessiontoken,
  theme = "light",
  title = "Neuroa",
  mountTimeoutMs = 8000,
  isOpen,
  onClose,
  onMaximize,
  width = 450,
  onMouseDown,
  isResizing,
}) {
  const containerRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState("");
  const lastIsOpen = useRef(isOpen);

  React.useEffect(() => {
    if (lastIsOpen.current !== isOpen) {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 400);
      lastIsOpen.current = isOpen;
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const { error, report, clearReport } = useChatKit({
    containerRef,
    isOpen,
    sessiontoken,
    theme,
    mountTimeoutMs,
  });

  const closeReport = () => {
    clearReport();
    setDownloadError("");
  };

  const downloadReport = async () => {
    if (!report.artifact || isDownloading) return;
    setIsDownloading(true);
    setDownloadError("");
    try {
      const backend = new URL(chatBackendUrl, window.location.href);
      const url = new URL(report.artifact.download_url, `${backend.href.replace(/\/$/, "")}/`);
      if (!/^https?:$/.test(url.protocol) || url.origin !== backend.origin) {
        throw new Error("La URL de descarga no pertenece al servidor del chat.");
      }
      const response = await fetch(url.href, {
        method: "GET",
        headers: { sessiontoken },
      });
      if (!response.ok) throw new Error(`No se pudo descargar el PDF (${response.status}).`);
      if (!/^application\/pdf(?:\s*;|$)/i.test(response.headers.get("content-type") || "")) {
        throw new Error("El servidor no devolvió un PDF.");
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = getReportPdfFilename(report.artifact);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      setDownloadError(e.message || "No se pudo descargar el PDF.");
    } finally {
      setIsDownloading(false);
    }
  };

  const isMaximized = isOpen && width > (window.innerWidth - 100);
  const artifact = report.artifact;

  return (
    <div
      className={`neuroa-side-panel ${isOpen ? "open" : ""} ${artifact ? "has-report" : ""} ${isResizing ? "resizing" : ""} ${isMaximized ? "maximized" : ""} ${isTransitioning ? "transitioning" : ""}`}
      style={!sessiontoken ? { display: 'none' } : undefined}
    >
      {!isMaximized && (
        <div className="neuroa-drag-handle" onMouseDown={onMouseDown}>
          <div className="neuroa-drag-indicator" />
        </div>
      )}

      <div className="neuroa-header">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="neuroa-title">{title}</div>
            <span className="neuroa-status-dot" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="neuroa-maximize-btn-styled" onClick={onMaximize}
            aria-label={width > 600 ? "Minimizar" : "Maximizar"}>
            {width > 600 ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
          </button>
          <button className="neuroa-close-btn-styled" onClick={onClose} aria-label="Cerrar">
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="neuroa-chat-content flex-1 flex flex-col min-h-0 bg-white/40">
        {!artifact && report.status === "generating" && (
          <div className="neuroa-report-progress" role="status">
            <strong>Generando reporte</strong>
            {report.progress && <span>{report.progress}</span>}
          </div>
        )}
        {!artifact && report.status === "error" && report.error && (
          <div className="neuroa-report-error" role="alert">{report.error}</div>
        )}
        {error ? (
          <div className="ck-error p-6 text-center">
            <div className="bg-red-50 text-red-500 p-4 rounded-xl border border-red-100 text-sm">{error}</div>
          </div>
        ) : (
          <div className="neuroa-body" ref={containerRef} />
        )}
      </div>
      {artifact && (
        <section className="neuroa-report-column" aria-label="Reporte generado">
          <div className="neuroa-header">
            <div className="neuroa-title">{artifact.title}</div>
            <button type="button" className="neuroa-close-btn-styled" onClick={closeReport} aria-label="Cerrar reporte">
              <FiX className="w-5 h-5" />
            </button>
          </div>
          {report.status === "updating" && (
            <div className="neuroa-report-progress" role="status">
              Actualizando reporte{report.progress ? `: ${report.progress}` : "..."}
            </div>
          )}
          {report.status === "error" && report.error && <div className="neuroa-report-error" role="alert">{report.error}</div>}
          <iframe title={artifact.title} srcDoc={artifact.html_content} sandbox="" referrerPolicy="no-referrer" />
          <div className="neuroa-report-actions">
            <button
              type="button"
              className={`neuroa-download-btn ${isDownloading ? "downloading" : ""}`}
              onClick={downloadReport}
              disabled={isDownloading}
              aria-label={isDownloading ? "Descargando PDF" : "Descargar PDF"}
              title={isDownloading ? "Descargando PDF" : "Descargar PDF"}
            >
              {isDownloading ? (
                <FiLoader className="neuroa-download-spinner" aria-hidden="true" />
              ) : (
                <FiDownload className="neuroa-download-icon" aria-hidden="true" />
              )}
            </button>
            {downloadError && <span className="neuroa-report-error" role="alert">{downloadError}</span>}
          </div>
        </section>
      )}
    </div>
  );
}
